import type { BlockDto } from '@klickibunti/pagebuilder-react';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import type { CtaAction } from '@/types/ctaAction';

/**
 * The five `widgets.hero-card-*` components an Editor can place in the Hero's
 * `content` dynamic zone, alongside `widgets.location-card`.
 *
 * They are deliberately NOT in `WIDGET_REGISTRY`: `HeroComponent` hand-picks
 * its slot child and renders it as a direct flex child so the card carries the
 * hero's own slot geometry, which `SlotRenderer`'s fixed wrapper would bury two
 * divs deep. They are also only ever valid inside `widgets.hero.content`, so a
 * `blockMap` entry would make them renderable from any zone.
 */
export const HERO_CARD_TYPES = [
  'widgets.hero-card-text',
  'widgets.hero-card-text-centered',
  'widgets.hero-card-tabbed-home',
  'widgets.hero-card-tabbed-city',
  'widgets.hero-card-prices',
  'widgets.hero-card-search-centered',
] as const;

export type HeroCardType = (typeof HERO_CARD_TYPES)[number];

/**
 * The cards that render a location search. Their autocomplete dropdown opens
 * below the panel and can extend past the Hero's own box, so the Hero has to
 * stop clipping itself when one of these fills the slot — see
 * `HeroComponent`. Knowledge of which cards carry a search lives here with the
 * cards rather than in the Hero.
 */
export const HERO_CARD_TYPES_WITH_SEARCH: readonly HeroCardType[] = [
  'widgets.hero-card-tabbed-home',
  'widgets.hero-card-prices',
  'widgets.hero-card-search-centered',
];

/**
 * Cards the Hero renders full-width and centered instead of in the narrow left
 * slot, with the Hero's own h1 moved inside the panel. Only the Location
 * Search Result page uses this today (10.02.11); every other card keeps
 * `SLOT_CLASS`.
 */
export const HERO_CARD_TYPES_FULL_WIDTH: readonly HeroCardType[] = [
  'widgets.hero-card-search-centered',
];

export function heroCardIsFullWidth(block: BlockDto): boolean {
  return (HERO_CARD_TYPES_FULL_WIDTH as readonly string[]).includes(
    block.componentType,
  );
}

export function heroCardHasSearch(block: BlockDto): boolean {
  return (HERO_CARD_TYPES_WITH_SEARCH as readonly string[]).includes(
    block.componentType,
  );
}

export function isHeroCardBlock(
  block: BlockDto,
): block is BlockDto & { componentType: HeroCardType } {
  return (HERO_CARD_TYPES as readonly string[]).includes(block.componentType);
}

/**
 * What `HeroComponent` passes every card besides its config: the hero's own
 * slot geometry, plus the block identity `SlotRenderer` would otherwise stamp.
 * These blocks bypass `SlotRenderer`, so each card root carries them itself.
 */
export interface HeroCardRootProps {
  'className'?: string;
  'data-strapi-block-type'?: string;
  'data-strapi-block-id'?: number;
}

/** Which of a tabbed card's two tabs opens first. */
export type PreselectedTab = 'one' | 'two';

// ── Config (raw CMS shape — every optional field really can be absent) ───────

/**
 * Shared by `widgets.hero-card-text` and `widgets.hero-card-text-centered`.
 * The two schemas carry the same fields; only the renderer's `align` and
 * `titleSize` differ, which is what makes the Editor's component choice the
 * carrier of the typography (SH-312 Q3/Q4 — no typography field exists).
 */
export interface HeroCardTextConfig extends BlockDto {
  title?: string;
  body?: BlocksContent;
  ctas?: CtaAction[];
  /** Resolved backend-side through the 70.07 chain (widget → location → domain). */
  phone?: string;
  /** Resolved Matelso display label, hard-paired with `phone`. */
  matelsoPhoneLabel?: string;
}

/** Tab 2 ("Nachhilfe Online") — structurally identical on both tabbed cards. */
export interface HeroCardOnlineTabConfig {
  tabTwoLabel?: string;
  tabTwoAnchor?: string;
  tabTwoBody?: BlocksContent;
  tabTwoCtas?: CtaAction[];
  tabTwoPostCtaText?: BlocksContent;
  tabTwoPostCtaPhone?: string;
  tabTwoPostCtaMatelsoPhoneLabel?: string;
  tabTwoPostCtaLinkLabel?: string;
  tabTwoPostCtaLinkAnchor?: string;
}

export interface HeroCardTabbedHomeConfig
  extends BlockDto, HeroCardOnlineTabConfig {
  tabOneLabel?: string;
  tabOneAnchor?: string;
  tabOneTitle?: string;
  tabOneSearchPlaceholder?: string;
  tabOneSearchButtonLabel?: string;
  tabOneSearchAnchor?: string;
  tabOneSearchTracking?: EventTrackingResolved[];
  tabOneBody?: BlocksContent;
  tabOneCtas?: CtaAction[];
  tabOnePostCtaText?: BlocksContent;
  tabOnePostCtaPhone?: string;
  tabOnePostCtaMatelsoPhoneLabel?: string;
  tabOnePostCtaLinkLabel?: string;
  tabOnePostCtaLinkAnchor?: string;
  preselectedTab?: PreselectedTab;
}

export interface HeroCardTabbedCityConfig
  extends BlockDto, HeroCardOnlineTabConfig {
  tabOneLabel?: string;
  tabOneAnchor?: string;
  tabOneTitle?: string;
  tabOneBody?: BlocksContent;
  tabOneCtas?: CtaAction[];
  tabOnePostCtaText?: BlocksContent;
  tabOnePostCtaPhone?: string;
  tabOnePostCtaMatelsoPhoneLabel?: string;
  tabOnePostCtaLinkLabel?: string;
  tabOnePostCtaLinkAnchor?: string;
  preselectedTab?: PreselectedTab;
}

export interface HeroCardPricesConfig extends BlockDto {
  title?: string;
  body?: BlocksContent;
  searchPlaceholder?: string;
  searchButtonLabel?: string;
  searchAnchor?: string;
  searchTracking?: EventTrackingResolved[];
  /** Lead-in line above the call CTA, e.g. "Wir helfen Ihnen gerne telefonisch:". */
  callCtaText?: string;
  phone?: string;
  matelsoPhoneLabel?: string;
}

/**
 * The centered, full-width search card. Same search fields as the Prices card
 * — the two are the centered and left-aligned variants of one design, split
 * into two schemas so the Editor's component choice carries the layout, the
 * way `hero-card-text` / `hero-card-text-centered` already do.
 *
 * It has no `title` of its own: the Hero's `title` is the page's h1 and is
 * rendered inside this card's panel.
 */
export interface HeroCardSearchCenteredConfig extends BlockDto {
  body?: BlocksContent;
  searchPlaceholder?: string;
  searchButtonLabel?: string;
  searchAnchor?: string;
  searchTracking?: EventTrackingResolved[];
}

// ── Resolved (normalised — optionals collapsed to '' / [] / null) ────────────

export interface HeroCardTextResolved {
  title: string;
  body: BlocksContent | null;
  ctas: CtaAction[];
  phone: string;
  matelsoPhoneLabel: string;
}

/**
 * What a tab renders below its CTAs: a rich-text line, a phone link and an
 * on-page link. Every part is optional; all three empty renders nothing.
 */
export interface HeroCardPostCtaResolved {
  text: BlocksContent | null;
  phone: string;
  matelsoPhoneLabel: string;
  linkLabel: string;
  /** Anchor name on the same page; the `#` is added by the renderer. */
  linkAnchor: string;
}

export interface HeroCardOnlineTabResolved {
  label: string;
  /** Fragment that opens this tab from an external link; '' when unset. */
  anchor: string;
  body: BlocksContent | null;
  ctas: CtaAction[];
  postCta: HeroCardPostCtaResolved;
}

export interface HeroCardSearchResolved {
  placeholder: string;
  buttonLabel: string;
  /**
   * Where the submit link points, so the search page is reachable by a
   * crawler. Stored without a leading slash (`standorte/suche`); the renderer
   * roots it and falls back to the search page when empty.
   */
  anchor: string;
  tracking: EventTrackingResolved[];
}

export interface HeroCardTabbedHomeResolved {
  tabOneLabel: string;
  tabOneAnchor: string;
  tabOneTitle: string;
  tabOneSearch: HeroCardSearchResolved;
  tabOneBody: BlocksContent | null;
  tabOneCtas: CtaAction[];
  tabOnePostCta: HeroCardPostCtaResolved;
  tabTwo: HeroCardOnlineTabResolved;
  preselectedTab: PreselectedTab;
}

export interface HeroCardTabbedCityResolved {
  tabOneLabel: string;
  tabOneAnchor: string;
  tabOneTitle: string;
  tabOneBody: BlocksContent | null;
  tabOneCtas: CtaAction[];
  tabOnePostCta: HeroCardPostCtaResolved;
  tabTwo: HeroCardOnlineTabResolved;
  preselectedTab: PreselectedTab;
}

export interface HeroCardPricesResolved {
  title: string;
  body: BlocksContent | null;
  search: HeroCardSearchResolved;
  callCtaText: string;
  phone: string;
  matelsoPhoneLabel: string;
}

export interface HeroCardSearchCenteredResolved {
  body: BlocksContent | null;
  search: HeroCardSearchResolved;
}
